// trailersStore.ts
import { create } from "zustand";
import type { CustomAlertProps } from "../../components/base/CustomAlert";
import { getError } from "../../utils/getError";
import type { TFunction } from "i18next";
import { type UseFormReset } from "react-hook-form";
import api, { apiFormData, isAbortError } from "../../utils/api";
import type { CancelTokenSource } from "axios";
import axios from "axios";
import { getAdminFetchGeneration } from "../../utils/adminFetchGeneration";
import { useVehiclesSocketStore } from "./vehiclesSocketStore";
import { fetchDevicesFromAPI } from "../../hooks/useVehiclesSocketSubscription";

const refreshMapDevices = () => {
  fetchDevicesFromAPI(undefined, true).then((devices) => {
    if (devices) useVehiclesSocketStore.getState().refreshDevicesData(devices);
  });
};

export interface Trailer {
  id: string;
  name?: string;
  icon?: string;
  image?: string;
  description?: string;
  plate: string;
  brand?: string;
  model?: string;
  year?: number;
  shipment_details?: string[];
  maximumWeight?: string;
  type?: string;
  status_id?: string;
  status?: {
    id: string;
    name: string;
  };
  condition?: string;
  vin?: string;
  serialNumber?: string;
  capacity?: string;
  axles?: string;
  length?: string;
  width?: string;
  height?: string;
  brakeType?: string;
  suspensionType?: string;
  registration?: string;
  insurance?: string;
  permit?: string;
  createdAt?: { _seconds: number; _nanoseconds: number };
  updatedAt?: { _seconds: number; _nanoseconds: number };
  inspectionDate?: { _seconds: number; _nanoseconds: number };
  nextInspectionDate?: { _seconds: number; _nanoseconds: number };
  vehicle?: { name: string; plate: string; image?: string };
  vehicle_id?: string;
  device_id?: string; // Conexión directa al device
  device?: { id: string; name?: string; identifier: string };
  lastAddress?: string;
  attached?: boolean;
  collections?: string[];
}

interface TrailersStore {
  trailers: Trailer[];
  availableTrailers: Trailer[];
  loading: boolean;
  error: string | null;
  meta?: {
    error: string | null;
    count: number;
    status: number;
    currentPage: number;
    totalPages: number;
    totalItems: number;
    url: string;
  };
  cancelToken: CancelTokenSource | null;
  fetchTrailers: (params?: {
    limit?: number;
    page?: number;
    offset?: number;
    status?: string;
    type?: string;
    search?: string;
  }) => Promise<void>;
  updateTrailerInStore: (trailerId: string, updatedTrailer: Partial<Trailer>) => void;
  addTrailerToStore: (trailer: Trailer) => void;
  setAvailableTrailers: (trailers: Trailer[]) => void;
  updateTrailerStatus: (
    trailerId: string,
    statusId: string
  ) => Promise<{ success: boolean; data?: Trailer; error?: string }>;
  deleteTrailer: (
    trailerId: string
  ) => Promise<{ success: boolean; error?: string }>;
}

export const useTrailersStore = create<TrailersStore>((set, get) => ({
  trailers: [],
  availableTrailers: [],
  loading: false,
  error: null,
  meta: undefined,
  cancelToken: null,

  fetchTrailers: async (params = {}) => {
    const { cancelToken, loading } = get();
    if (cancelToken) {
      cancelToken.cancel("Operation canceled due to new request.");
    }

    if (loading && !cancelToken) return;

    const fetchGen = getAdminFetchGeneration();
    const newCancelToken = axios.CancelToken.source();
    set({ loading: true, error: null, cancelToken: newCancelToken });

    try {
      const { data } = await api.get("/remolquesList", {
        params: {
          limit: params.limit ?? 8,
          page: params.page ?? 1,
          ...params,
        },
        cancelToken: newCancelToken.token,
      });
      if (getAdminFetchGeneration() !== fetchGen) return;
      set({
        trailers: data.data || [],
        meta: data.meta,
        loading: false,
        cancelToken: null,
      });
    } catch (err: any) {
      if (axios.isCancel(err) || isAbortError(err) || getAdminFetchGeneration() !== fetchGen) {
        set({ loading: false, cancelToken: null });
        return;
      }
      set({
        error: getError(err),
        loading: false,
        cancelToken: null,
      });
    }
  },

  updateTrailerInStore: (trailerId: string, updatedTrailer: Partial<Trailer>) => {
    set((state) => ({
      trailers: state.trailers.map((trailer) =>
        trailer.id === trailerId ? { ...trailer, ...updatedTrailer } : trailer
      ),
    }));
  },

  addTrailerToStore: (trailer: Trailer) => {
    set((state) => ({
      trailers: [trailer, ...state.trailers],
      meta: state.meta
        ? {
            ...state.meta,
            count: state.meta.count + 1,
            totalItems: state.meta.totalItems + 1,
          }
        : undefined,
    }));
  },

  setAvailableTrailers: (trailers: Trailer[]) => {
    set({ availableTrailers: trailers });
  },

  updateTrailerStatus: async (trailerId: string, statusId: string) => {
    try {
      const formData = new FormData();
      formData.append("status_id", statusId);

      const { data } = await apiFormData.put("/remolques", formData, {
        params: { remolque_id: trailerId },
      });

      const updatedTrailer = data?.data ?? data;

      if (updatedTrailer) {
        get().updateTrailerInStore(trailerId, { status_id: statusId });
      }

      refreshMapDevices();
      return { success: true, data: updatedTrailer };
    } catch (error) {
      console.error("Error updating trailer status:", error);
      return { success: false, error: getError(error) };
    }
  },

  deleteTrailer: async (trailerId: string) => {
    try {
      await api.delete("/remolques", { params: { remolque_id: trailerId } });
      set((state) => ({
        trailers: state.trailers.filter((tr) => tr.id !== trailerId),
      }));
      refreshMapDevices();
      return { success: true };
    } catch (error) {
      console.error("Error deleting trailer:", error);
      return { success: false, error: getError(error) };
    }
  },
}));

export const updateTrailer = async (
  trailerId: string,
  formData: FormData,
  showAlert: (config: CustomAlertProps) => void,
  setLoadingSubmit: (loading: boolean) => void,
  t: TFunction<"translation", undefined>,
  onClose: () => void,
  reset?: UseFormReset<any>
) => {
  try {
    const { data } = await apiFormData.put("/remolques", formData, {
      params: { remolque_id: trailerId },
    });

    if (data.data) {
      const updatedData = { ...data.data };
      if (updatedData.img_url) {
        const separator = updatedData.img_url.includes("?") ? "&" : "?";
        updatedData.img_url = `${updatedData.img_url}${separator}t=${Date.now()}`;
      }
      useTrailersStore.getState().updateTrailerInStore(trailerId, updatedData);
    }

    refreshMapDevices();
    setLoadingSubmit(false);
    showAlert({
      title: t("Exito"),
      text: t("Remolque actualizado exitosamente"),
      icon: "success",
      confirmButtonText: "OK",
      confirmButtonColor: "#2563eb",
      onConfirm: () => {
        onClose();
        if (reset) reset();
      },
      showCancelButton: false,
    });
  } catch (error) {
    setLoadingSubmit(false);
    showAlert({
      title: t("Error"),
      text: getError(error, t),
      icon: "error",
      confirmButtonText: "OK",
      confirmButtonColor: "#2563eb",
      onConfirm: () => {},
    });
  }
};

export const createTrailer = async (
  formData: FormData,
  showAlert: (config: CustomAlertProps) => void,
  setLoadingSubmit: (loading: boolean) => void,
  t: TFunction<"translation", undefined>,
  onClose: () => void,
  reset?: UseFormReset<any>
) => {
  try {
    const { data } = await apiFormData.post("/remolques", formData);

    if (data.data) {
      useTrailersStore.getState().addTrailerToStore(data.data);
    }

    refreshMapDevices();
    setLoadingSubmit(false);
    showAlert({
      title: t("Exito"),
      text: t("Remolque creado exitosamente"),
      icon: "success",
      confirmButtonText: "OK",
      confirmButtonColor: "#2563eb",
      onConfirm: () => {
        onClose();
        if (reset) reset();
      },
      showCancelButton: false,
    });
  } catch (error) {
    setLoadingSubmit(false);
    showAlert({
      title: t("Error"),
      text: getError(error, t),
      icon: "error",
      confirmButtonText: "OK",
      confirmButtonColor: "#2563eb",
      onConfirm: () => {},
    });
  }
};

export const getAvailableTrailers = async (): Promise<Trailer[]> => {
  try {
    const { data } = await api.get("/remolquesForVehicle");
    if (Array.isArray(data)) {
      return data;
    }
    if (data?.data?.data && Array.isArray(data.data.data)) {
      return data.data.data;
    }
    if (data?.data && Array.isArray(data.data)) {
      return data.data;
    }
    return [];
  } catch (error) {
    console.error("Error getting available trailers:", error);
    return [];
  }
};

export const getTrailerById = async (
  trailerId: string
): Promise<{ success: boolean; data?: Trailer; error?: string }> => {
  try {
    const { data } = await api.get("/remolquesById", {
      params: { id: trailerId },
    });
    if (data?.data) {
      return { success: true, data: data.data.data };
    }
    return { success: false, error: "No se encontró el remolque" };
  } catch (error) {
    console.error("Error getting trailer by id:", error);
    return { success: false, error: getError(error) };
  }
};